import logger from '../common/logger';

/**
 * Convert language to CJS locale format
 * @param {string} language - Language from envContext (e.g., 'en-US', 'de-DE')
 * @returns {string} CJS locale format (e.g., 'en_US', 'de_DE')
 */
const getCjsLocale = (language) => {
  const lang = language || 'en-US';
  return lang.replace('-', '_'); // Convert en-US to en_US for CJS
};

/**
 * Check if CJS SDKs are ready
 */
const isCjsReady = () =>
  typeof window !== 'undefined' &&
  typeof window.Adp === 'function' &&
  typeof window.CjsTracker !== 'undefined';

/**
 * Wait for CJS SDKs to be ready (since they load with defer attribute)
 */
const waitForCjsReady = (timeout = 10000) =>
  new Promise((resolve, reject) => {
    if (isCjsReady()) {
      resolve();
      return;
    }

    const startTime = Date.now();
    const interval = setInterval(() => {
      if (isCjsReady()) {
        clearInterval(interval);
        resolve();
      } else if (Date.now() - startTime > timeout) {
        clearInterval(interval);
        reject(new Error('CJS SDKs not ready after timeout'));
      }
    }, 100); // Check every 100ms
  });

/**
 * Handle consent preferences - set defaults if pending user action
 * @param {Object} result - Result from loadUserConsentPreferences
 */
export const handleConsentPreferences = (result) => {
  if (!result || result.status?.code === 2) {
    const defaultConsent = [
      { consentId: 'ADSK_PUD_CONTRACTUAL_NECESSITY_DESKTOP', optIn: true },
      { consentId: 'ADSK_PUD_OPTIMIZATION_IMPROVEMENT_DESKTOP', optIn: true },
      { consentId: 'ADSK_PUD_GO_TO_MARKET_DESKTOP', optIn: true }
    ];

    if (window.adsk?.cjs?.adp?.setCurrentUserConsentPreferences) {
      window.adsk.cjs.adp.setCurrentUserConsentPreferences(defaultConsent);
      logger.info('[CJS] Set default consent preferences');
    } else {
      logger.error('[CJS] Cannot set consent - CJS not ready');
    }
  }
};

/**
 * Initialize CJS tracking
 * Called once when SSR app mounts
 *
 * @param {string} clientId - The PKCE client ID (used as CJS client_id)
 * @param {string} env - Environment ('dev', 'stg', or 'prd')
 * @param {string} language - Language from envContext (e.g., 'en-US', 'de-DE')
 */
export const initializeCjsTracking = async (clientId, env, language) => {
  try {
    logger.info('[CJS] Starting initialization...');

    // Wait for SDKs to be ready (loaded from HTML scripts)
    await waitForCjsReady();

    const locale = getCjsLocale(language);

    logger.info('[CJS] Environment:', env);
    logger.info('[CJS] Client ID:', clientId);

    // Initialize CJS with ADP
    const adp = window.Adp();

    // Enable debug mode for non-production
    const enableDebug =
      env !== 'prd' || window.location.search.includes('adp_debug=true');
    if (enableDebug) {
      window.adp_debug = true;
      logger.info('[CJS] Debug mode enabled');
    }

    const tracker = window.CjsTracker.init(adp)
      .setEnv(env)
      // Remove .trackClickElements() from here - must be called after load()
      .defaultEventAttributes({
        event_generator: 'dotcom-support-us'
      })
      .defaultEventData({
        page: {
          page_category: 'support',
          page_locale: locale
        }
      });

    // Enable debug logging for non-prod
    if (enableDebug) {
      tracker.enableDebug();
    }

    await tracker.load({ id: clientId });

    // ✅ CRITICAL: Call trackClickElements() AFTER load completes
    // This ensures the SDK is fully initialized before setting up DOM listeners
    tracker.trackClickElements();
    logger.info('[CJS] Auto-capture enabled');

    // ✅ Load consent preferences for anonymous users (before authentication)
    try {
      if (window.adsk?.cjs?.loadUserConsentPreferences) {
        const consentResult =
          await window.adsk.cjs.loadUserConsentPreferences();
        handleConsentPreferences(consentResult);
        logger.info('[CJS] Anonymous user consent preferences loaded');
      }
    } catch (error) {
      // Non-critical - log but don't fail initialization
      if (env !== 'prd') {
        logger.error('[CJS] Failed to load anonymous consent:', error);
      }
    }

    logger.info('[CJS] ✅ Initialization complete');
    logger.info('[CJS] window.adsk.cjs is now available');

    return true;
  } catch (error) {
    logger.error('[CJS] ❌ Initialization failed:', error);
    return false;
  }
};

/**
 * Check if CJS is ready for use
 */
export const isCjsTrackerReady = () => !!window.adsk?.cjs;

/**
 * Set authenticated user and load consent preferences
 * @param {string} userid - User ID from authentication
 * @param {string} accessToken - Access token for loading consent
 * @param {string} env - Environment (prd, stg, dev)
 */
export const setAuthenticatedUser = async (userid, accessToken, env) => {
  // Only proceed if CJS is ready
  if (!window.adsk?.cjs) {
    logger.warn('[CJS] Cannot set user - CJS not initialized yet');
    return;
  }

  try {
    // Set the user ID
    window.adsk.cjs.setUser({ user_id: userid });
    logger.info('[CJS] Set user ID:', userid);

    // Load user consent preferences with access token
    if (window.adsk.cjs.loadUserConsentPreferences) {
      const result = await window.adsk.cjs.loadUserConsentPreferences(
        accessToken
      );

      // Handle consent preferences (set defaults if needed)
      handleConsentPreferences(result);

      logger.info('[CJS] ✅ User consent preferences loaded');
    } else {
      logger.error('[CJS] loadUserConsentPreferences not available');
    }
  } catch (error) {
    // Log error in non-production environments
    if (env !== 'prd') {
      logger.error('[CJS] Failed to set user or load consent:', error);
    } else {
      logger.warn('[CJS] Failed to set user or load consent');
    }
  }
};
